import type { FetchResult } from '@apollo/client';
import { WorklistItemStatus } from 'generated/graphql';
import type { GetBaseViewerDataQuery } from 'generated/graphql';

// Bounds how long a missing "dicom study ready" event (EN-13939) can block caching.
export const STUDY_READY_WAIT_MS = 24 * 60 * 60 * 1000;

// Orders whose exam has not been performed or delivered yet, so their images are not complete.
const NOT_READY_STATUSES: ReadonlyArray<WorklistItemStatus> = [
  WorklistItemStatus.InTransit,
  WorklistItemStatus.Scheduled,
];

interface StudyReadiness {
  created: Date | string;
  readyAt?: Nullable<Date | string>;
}

interface ShouldCacheBaseViewerDataResultParams {
  result: FetchResult<GetBaseViewerDataQuery>;
  shouldWaitForStudyReady: boolean;
}

export function shouldCacheBaseViewerDataResult({
  result,
  shouldWaitForStudyReady,
}: ShouldCacheBaseViewerDataResultParams): boolean {
  return !result.data?.worklistItems?.items.some(
    (item) =>
      NOT_READY_STATUSES.includes(item.status) ||
      (shouldWaitForStudyReady &&
        [...item.studies, ...(item.linkedStudies ?? [])].some(isAwaitingStudyReady))
  );
}

function isAwaitingStudyReady({ created, readyAt }: StudyReadiness): boolean {
  if (readyAt != null) return false;
  return Date.now() - new Date(created).getTime() < STUDY_READY_WAIT_MS;
}
